import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router'

import { useNotifications } from 'app/application/application'
import { Form } from 'app/common/presentation'
import { useConnections, useFacebookConnectionApi } from 'app/connections/application'
import { FacebookAdAccount } from 'app/connections/types'
import { FacebookPixel } from 'app/connections/types/facebook'

import { Button } from 'components'

import AdAccounts from '../Accounts'
import Authorization from '../Authorization'
import setupStyles from '../Setup.module.scss'
import { StepContext } from './StepWithConnectionAndAccounts'

type AccountPixel = {
  account: FacebookAdAccount
  pixel: FacebookPixel
}

const FacebookSettings = () => {
  const navigate = useNavigate()
  const { createNotification, te } = useNotifications()
  const { accounts, connection } = useOutletContext<StepContext<FacebookAdAccount>>()
  const { getPixels } = useFacebookConnectionApi()
  const { saveConnection } = useConnections()
  const { t } = useTranslation('connections')

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [accountPixels, setAccountPixels] = useState<AccountPixel[]>([])
  const [selectedPixel, setSelectedPixel] = useState<AccountPixel | null>(null)
  const [adId, setAdId] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)

  useEffect(
    () =>
      void getPixels(accounts.map((account) => account.externalId))
        .then((result) => {
          if (result.ok) {
            setAccountPixels(
              Object.entries(result.val).flatMap(([externalAccountId, pixels]) => {
                const account = accounts.find(
                  (account) => account.externalId === externalAccountId,
                )!
                return pixels.map((pixel) => ({ account, pixel }))
              }),
            )
          } else {
            createNotification({
              message: result.val.message,
              title: te(`http.${result.val.code}`),
              type: 'error',
            })
          }
        })
        .finally(() => setIsLoading(false)),
    [accounts, createNotification, getPixels, te],
  )

  const handleSave = useCallback(() => {
    setIsSaving(true)

    const connectionConfig = {
      accounts,
      pixelId: selectedPixel?.pixel.id ?? '',
      adId,
    }

    void saveConnection(connection, connectionConfig)
      .then((result) => {
        if (result.ok) {
          createNotification({
            message: t('update.success.message', {
              name: t(`available.${connection.name}.displayName`),
            }),
            title: t('update.success.title'),
            type: 'success',
          })

          void navigate(`/connections/${connection.name}/setup`)
        } else {
          createNotification({
            message: result.val.message,
            title: te(`http.${result.val.code}`),
            type: 'error',
          })
        }
      })
      .finally(() => setIsSaving(false))
  }, [
    accounts,
    adId,
    createNotification,
    navigate,
    saveConnection,
    selectedPixel,
    connection,
    t,
    te,
  ])

  return (
    <div>
      <div className={setupStyles.form}>
        <Authorization authorizationState={'VALID'} connection={connection} />
        <AdAccounts accounts={accounts} connectionName={connection.name} />
        <Form.DropDownFormRow
          data={accountPixels}
          description={t(`setup.${connection.name}.config.pixelId.description`)}
          loading={isLoading}
          onSelect={(accountPixel) => setSelectedPixel(accountPixel)}
          renderValueContent={(accountPixel) =>
            `[${accountPixel.account.name}] ${accountPixel.pixel.name} (${accountPixel.pixel.id})`
          }
          selected={selectedPixel}
          title={t(`setup.${connection.name}.config.pixelId.title`)}
        />
        <Form.InputFormRow
          description={t(`setup.${connection.name}.config.adId.description`)}
          onChange={setAdId}
          placeholder={t(`setup.${connection.name}.config.adId.title`)}
          title={t(`setup.${connection.name}.config.adId.title`)}
          value={adId}
        />
      </div>
      <div className={setupStyles.actions}>
        <Button onClick={() => void navigate(-1)} variant="secondary">
          {t('setup.actions.back')}
        </Button>
        <Button
          disabled={!selectedPixel || !adId}
          loading={isSaving}
          onClick={(e) => {
            e.stopPropagation()

            handleSave()
          }}
        >
          {t('setup.actions.finish')}
        </Button>
      </div>
    </div>
  )
}

export default FacebookSettings
